// State module for dashboard information

import {
  formatDateToInputDateTime,
  getCurrentLocalDateTime,
} from '@/business/utils/dates';

export default (store) => {
  store.registerModule('dashboardDispatcher', {
    namespaced: true,
    state: {
      lastRefreshItemsDate: new Date(Date.now()),
      filters: {
        dateMin: formatDateToInputDateTime(
          new Date(Date.now()).setHours(0, 0, 0, 0)
        ),
        dateMaxDay: formatDateToInputDateTime(
          new Date(Date.now()).setHours(23, 59, 59, 0)
        ),
        dateMax: getCurrentLocalDateTime(),
        entities: [],
        technicians: [],
        properties: [],
        openHours: [],
      },
      selected: {
        label: null,
      },
      refresh: false,
    },
    getters: {
      dateMin: (state) => state.filters.dateMin,
      dateMaxDay: (state) => state.filters.dateMaxDay,
      lastRefreshItemsDate: (state) => state.lastRefreshItemsDate,
      active: (state) => state.selected.label,
      dateMax: (state) => state.filters.dateMax,
      refresh: (state) => state.refresh,
      technicians: (state) => state.filters.technicians,
      properties: (state) => state.filters.properties,
      entities: (state) => state.filters.entities,
      openHours: (state) => state.filters.openHours,
      signalisationFilters: (state, getters, rootState, rootGetters) => {
        const res = [
          {
            member: 'requests.signal_datetime',
            operator: 'afterDate',
            values: [getters.dateMin],
          },
        ];
        if (
          rootGetters['entities/current'] &&
          rootGetters['entities/current'].length
        ) {
          res.push({
            dimension: 'properties.navFullId',
            operator: 'contains',
            values: rootGetters['entities/current'].map(
              (item) => item.nav_full_id
            ),
          });
        }
        if (getters.entities && getters.entities.length) {
          res.push({
            dimension: 'properties.navFullId',
            operator: 'contains',
            values: getters.entities,
          });
        }
        if (state.filters.properties.length) {
          res.push({
            dimension: 'properties.scheduled',
            operator: 'equals',
            values: state.filters.properties,
          });
        }
        return res;
      },
      filters: (state, getters) => {
        const res = Array.from(getters.signalisationFilters);

        if (state.filters.technicians.length) {
          res.push({
            dimension: 'technicians.scheduled',
            operator: 'equals',
            values: state.filters.technicians,
          });
        }
        return res;
      },
      graphFilters(state, getters) {
        const filters = Array.from(getters.filters);

        if (state.filters.openHours.length) {
          filters.push({
            dimension: 'requests.openhours',
            operator: 'equals',
            values: state.filters.openHours,
          });
          return filters;
        }
        return getters.filters;
      },
      flowFilters(state, getters) {
        const filters = Array.from(getters.graphFilters);
        let dateFilter = filters[0];
        dateFilter = {
          or: [
            {
              and: [
                {
                  member: 'requests.planned_datetime',
                  operator: 'notSet',
                },
                dateFilter,
              ],
            },
            {
              and: [
                {
                  member: 'requests.planned_datetime',
                  operator: 'set',
                },
                {
                  member: 'requests.planned_datetime',
                  operator: 'afterDate',
                  values: [getters.dateMin],
                },
                {
                  member: 'requests.planned_datetime',
                  operator: 'beforeDate',
                  values: [getters.dateMaxDay],
                },
              ],
            },
          ],
        };
        filters[0] = dateFilter;
        return filters;
      },
      signalisationGraphFilters(state, getters) {
        if (state.filters.openHours.length) {
          const filters = Array.from(getters.filters);
          filters.push({
            dimension: 'requests.openhours',
            operator: 'equals',
            values: state.filters.openHours,
          });
          return filters;
        }
        return getters.signalisationFilters;
      },
      isFiltersDirty(state, getters) {
        return (
          getters.openHours.length +
          getters.properties.length +
          getters.technicians.length +
          getters.entities.length
        );
      },
    },
    mutations: {
      SET_ALL_NULL: (state) => {
        state.filters.dateMax = null;
        state.filters.dateMin = null;
        state.filters.technicians = null;
        state.filters.properties = null;
        state.filters.entities = null;
      },
      SET_DATE_MIN: (state, dateMin) => {
        state.filters.dateMin = dateMin;
      },
      SET_DATE_MAX: (state, dateMax) => {
        state.filters.dateMax = dateMax;
      },
      SET_TECHNICIAN_FILTER: (state, values) => {
        state.filters.technicians = values;
      },
      SET_OPEN_HOURS_FILTER: (state, values) => {
        state.filters.openHours = values;
      },
      SET_PROPERTY_FILTER: (state, values) => {
        state.filters.properties = values;
      },
      SET_ENTITY_FILTER: (state, values) => {
        state.filters.entities = values;
      },
      TOGGLE_REFRESH: (state) => {
        state.refresh = !state.refresh;
      },
      SET_ACTIVE: (state, val) => {
        state.selected.label = val;
      },
      SET_LAST_REFRESH_DATE: (state) => {
        state.lastRefreshItemsDate = new Date(Date.now());
      },
    },
    actions: {
      refreshItems({ commit }) {
        commit('TOGGLE_REFRESH');
      },
      setDatesFilter({ commit }, payload) {
        if (payload.dateMin) commit('SET_DATE_MIN', payload.dateMin);
        if (payload.dateMax) commit('SET_DATE_MAX', payload.dateMax);
      },
      setActive({ commit }, payload) {
        commit('SET_ACTIVE', payload);
      },
      setTechnicianFilter({ commit }, values) {
        commit('SET_TECHNICIAN_FILTER', values);
      },
      setOpenHoursFilter({ commit }, values) {
        commit('SET_OPEN_HOURS_FILTER', values);
      },
      setPropertiesFilter({ commit }, values) {
        commit('SET_PROPERTY_FILTER', values);
      },
      setEntitiesFilter({ commit }, values) {
        commit('SET_ENTITY_FILTER', values);
      },
      clearFilters({ commit }) {
        commit('SET_ALL_NULL');
      },
    },
  });
};
